import OnchainID from '@onchain-id/solidity';
import { ApiKeyStamper } from '@turnkey/api-key-stamper';
import { TurnkeySigner } from '@turnkey/ethers';
import { TurnkeyClient } from '@turnkey/http';
import { ethers } from 'ethers';

const TURNKEY_BASE_URL = 'https://api.turnkey.com';

export type AssetTransferCreds = {
  organizationId: string;
  apiPublicKey: string;
  apiPrivateKey: string;
  walletAddress: string;
  relayerAddress: string;
  creWebhookUrl: string;
};

const PROD_HOSTNAME = 'admin.domxbridgetower.com';

// ─── ABIs ─────────────────────────────────────────────────────────────────────

const TOKEN_ABI = [
  'function transferWithAuthorization(address from, address to, uint256 value, bytes32 nonce, uint8 v, bytes32 r, bytes32 s)',
  'function balanceOf(address account) view returns (uint256)',
  'function decimals() view returns (uint256)',
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function version() view returns (string)',
  'function authorizationState(address authorizer, bytes32 nonce) view returns (bool)',
  'function paused() view returns (bool)',
  'function identityRegistry() view returns (address)',
];

const IDENTITY_REGISTRY_ABI = [
  'function isVerified(address _userAddress) view returns (bool)',
  'function identity(address _userAddress) view returns (address)',
  'function deleteIdentity(address _userAddress)',
  'function registerIdentity(address _userAddress, address _identity, uint16 _country)',
  'function isAgent(address _agent) view returns (bool)',
  'function addAgent(address _agent)',
  'function topicsRegistry() view returns (address)',
  'function issuersRegistry() view returns (address)',
];

const CLAIM_TOPICS_REGISTRY_ABI = [
  'function getClaimTopics() view returns (uint256[])',
];

const IDENTITY_ABI = [
  'function addKey(bytes32 _key, uint256 _purpose, uint256 _keyType) returns (bool)',
  'function keyHasPurpose(bytes32 _key, uint256 _purpose) view returns (bool)',
];

const TRUSTED_ISSUERS_REGISTRY_ABI = [
  'function isTrustedIssuer(address _issuer) view returns (bool)',
  'function addTrustedIssuer(address _issuer, uint256[] calldata _claimTopics)',
];

const IDENTITY_FORWARDER_ABI = [
  'function registerIdentityAndAddClaim(address _identityRegistry, address _userAddress, address _identity, uint16 _country, uint256 _topic, uint256 _scheme, address _issuer, bytes calldata _signature, bytes calldata _data, string calldata _uri)',
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function getRpcUrl(): string {
  if (process.env.REACT_APP_AVAX_RPC_URL)
    return process.env.REACT_APP_AVAX_RPC_URL;
  return window.location.hostname === PROD_HOSTNAME
    ? 'https://api.avax.network/ext/bc/C/rpc'
    : 'https://api.avax-test.network/ext/bc/C/rpc';
}

export function createClient(creds: AssetTransferCreds): TurnkeyClient {
  return new TurnkeyClient(
    { baseUrl: TURNKEY_BASE_URL },
    new ApiKeyStamper({
      apiPublicKey: creds.apiPublicKey,
      apiPrivateKey: creds.apiPrivateKey,
    }),
  );
}

function getDeploymentAddresses() {
  const implAuthority = process.env.REACT_APP_IDENTITY_IMPL_AUTHORITY;
  const claimIssuer = process.env.REACT_APP_CLAIM_ISSUER;
  const identityForwarder = process.env.REACT_APP_IDENTITY_FORWARDER;
  if (!implAuthority || !claimIssuer || !identityForwarder)
    throw new Error(
      'Missing env vars: REACT_APP_IDENTITY_IMPL_AUTHORITY, REACT_APP_CLAIM_ISSUER, REACT_APP_IDENTITY_FORWARDER',
    );
  return { implAuthority, claimIssuer, identityForwarder };
}

// ─── Balance ──────────────────────────────────────────────────────────────────

export type TokenBalanceResult = {
  balance: string;
  symbol: string;
  decimals: number;
};

export async function getTokenBalance(
  tokenAddress: string,
  walletAddress: string,
): Promise<TokenBalanceResult> {
  const provider = new ethers.JsonRpcProvider(getRpcUrl());
  const token = new ethers.Contract(tokenAddress, TOKEN_ABI, provider);
  const [balance, decimals, symbol] = await Promise.all([
    token.balanceOf(walletAddress) as Promise<bigint>,
    token.decimals() as Promise<bigint>,
    token.symbol() as Promise<string>,
  ]);
  return {
    balance: ethers.formatUnits(balance, Number(decimals)),
    symbol,
    decimals: Number(decimals),
  };
}

// ─── Full Transfer (identity setup + transferWithAuthorization) ───────────────

export type FullTransferResult = {
  txHash: string;
  from: string;
  to: string;
  tokens: string;
  nonce: string;
  blockNumber: number;
  creTriggered: boolean;
  identityRegistered: boolean;
};

export async function executeFullTransfer(
  creds: AssetTransferCreds,
  tokenAddress: string,
  toAddress: string,
  tokensHuman: string,
  assetId: string | number,
): Promise<FullTransferResult> {
  const countryCode = 840; // US — matches fulltransfer.ts
  const {
    implAuthority,
    claimIssuer: CLAIM_ISSUER,
    identityForwarder: IDENTITY_FORWARDER,
  } = getDeploymentAddresses();

  console.log(
    '[transfer] init — token:',
    tokenAddress,
    'to:',
    toAddress,
    'amount:',
    tokensHuman,
  );

  // Turnkey address lookup is case-sensitive; normalize all to EIP-55 checksum format
  const walletAddress = ethers.getAddress(creds.walletAddress);
  const relayerAddress = ethers.getAddress(creds.relayerAddress);
  toAddress = ethers.getAddress(toAddress);

  const client = createClient(creds);
  const provider = new ethers.JsonRpcProvider(getRpcUrl());
  const abiCoder = ethers.AbiCoder.defaultAbiCoder();

  console.log('[transfer] RPC:', getRpcUrl());
  console.log(
    '[transfer] treasury:',
    walletAddress,
    '| relayer:',
    relayerAddress,
  );

  const treasurySigner = new TurnkeySigner({
    client,
    organizationId: creds.organizationId,
    signWith: walletAddress,
  }).connect(provider);

  const relayerSigner = new TurnkeySigner({
    client,
    organizationId: creds.organizationId,
    signWith: relayerAddress,
  }).connect(provider);

  const token = new ethers.Contract(tokenAddress, TOKEN_ABI, relayerSigner);

  console.log('[transfer] fetching token metadata…');
  const [tokenName, decimalsRaw, isPaused] = await Promise.all([
    token.name() as Promise<string>,
    token.decimals() as Promise<bigint>,
    token.paused() as Promise<boolean>,
  ]);
  console.log(
    '[transfer] token:',
    tokenName,
    '| decimals:',
    decimalsRaw.toString(),
    '| paused:',
    isPaused,
  );
  if (isPaused) throw new Error('Token is paused. Cannot execute transfers.');

  let tokenVersion = '1';
  try {
    tokenVersion = (await token.version()) as string;
  } catch {
    /* ignore */
  }
  console.log('[transfer] token version:', tokenVersion);

  const decimals = Number(decimalsRaw);
  const registryAddress = await (token.identityRegistry() as Promise<string>);
  console.log('[transfer] identity registry:', registryAddress);
  const registry = new ethers.Contract(
    registryAddress,
    IDENTITY_REGISTRY_ABI,
    relayerSigner,
  ) as any;

  // ── Identity setup ──────────────────────────────────────────────────────────

  let identityRegistered = false;
  const recipientVerified = await (registry.isVerified(
    toAddress,
  ) as Promise<boolean>);
  console.log('[transfer] recipient verified:', recipientVerified);

  if (!recipientVerified) {
    const relayerAddress = await relayerSigner.getAddress();

    // Step 1: delete existing identity if present
    console.log('[identity] step 1 — checking existing identity…');
    const existingIdentity = await (registry.identity(
      toAddress,
    ) as Promise<string>);
    if (existingIdentity !== ethers.ZeroAddress) {
      console.log(
        '[identity] step 1 — deleting stale identity:',
        existingIdentity,
      );
      const deleteTx = await registry.deleteIdentity(toAddress);
      await deleteTx.wait();
      console.log('[identity] step 1 — deleted ✓');
    } else {
      console.log('[identity] step 1 — no existing identity, skipping delete');
    }

    // Step 2: deploy new IdentityProxy for the investor
    console.log(
      '[identity] step 2 — deploying IdentityProxy (implAuthority:',
      implAuthority,
      ')…',
    );
    const identityProxy = await new ethers.ContractFactory(
      OnchainID.contracts.IdentityProxy.abi,
      OnchainID.contracts.IdentityProxy.bytecode,
      relayerSigner,
    ).deploy(implAuthority, relayerAddress);
    await identityProxy.waitForDeployment();
    const newIdentityAddress = await identityProxy.getAddress();
    console.log(
      '[identity] step 2 — IdentityProxy deployed ✓',
      newIdentityAddress,
    );

    const newIdentity = new ethers.Contract(
      newIdentityAddress,
      IDENTITY_ABI,
      relayerSigner,
    ) as any;

    // Step 3: add management keys (forwarder + recipient)
    console.log('[identity] step 3 — adding management keys…');
    const forwarderKeyHash = ethers.keccak256(
      abiCoder.encode(['address'], [IDENTITY_FORWARDER]),
    );
    const recipientKeyHash = ethers.keccak256(
      abiCoder.encode(['address'], [toAddress]),
    );
    for (const [label, keyHash] of [
      ['forwarder', forwarderKeyHash],
      ['recipient', recipientKeyHash],
    ]) {
      try {
        const tx = await newIdentity.addKey(keyHash, 1, 1);
        await tx.wait();
        console.log(`[identity] step 3 — added ${label} key ✓`);
      } catch {
        console.log(
          `[identity] step 3 — ${label} key already exists, skipping`,
        );
      }
    }

    // Step 4: get KYC claim topic
    console.log('[identity] step 4 — fetching KYC claim topic…');
    const topicsRegistryAddress =
      await (registry.topicsRegistry() as Promise<string>);
    const topicsRegistry = new ethers.Contract(
      topicsRegistryAddress,
      CLAIM_TOPICS_REGISTRY_ABI,
      provider,
    );
    const claimTopics = await (topicsRegistry.getClaimTopics() as Promise<
      bigint[]
    >);
    const kycTopic = claimTopics[0];
    console.log('[identity] step 4 — KYC topic:', kycTopic.toString(), '✓');

    // Step 5: sign KYC claim with relayer
    console.log('[identity] step 5 — signing KYC claim…');
    const claimData = ethers.hexlify(ethers.toUtf8Bytes('KYC approved'));
    const dataHash = ethers.keccak256(
      abiCoder.encode(
        ['address', 'uint256', 'bytes'],
        [newIdentityAddress, kycTopic, claimData],
      ),
    );
    const claimSignature = await (relayerSigner as any).signMessage(
      ethers.getBytes(dataHash),
    );
    console.log('[identity] step 5 — claim signed ✓');

    // Step 6a: ensure IdentityForwarder is an agent on the registry
    console.log('[identity] step 6a — checking forwarder agent status…');
    const forwarderIsAgent = await (registry.isAgent(
      IDENTITY_FORWARDER,
    ) as Promise<boolean>);
    console.log('[identity] step 6a — forwarder isAgent:', forwarderIsAgent);
    if (!forwarderIsAgent) {
      console.log('[identity] step 6a — granting agent role to forwarder…');
      const addAgentTx = await registry.addAgent(IDENTITY_FORWARDER);
      await addAgentTx.wait();
      console.log('[identity] step 6a — agent role granted ✓');
    }

    // Step 6b: registerIdentityAndAddClaim via IdentityForwarder
    console.log(
      '[identity] step 6b — calling registerIdentityAndAddClaim via forwarder…',
    );
    console.log('[identity] step 6b — args:', {
      registryAddress,
      toAddress,
      newIdentityAddress,
      countryCode,
      kycTopic: kycTopic.toString(),
      CLAIM_ISSUER,
    });
    const forwarder = new ethers.Contract(
      IDENTITY_FORWARDER,
      IDENTITY_FORWARDER_ABI,
      relayerSigner,
    ) as any;
    const registerTx = await forwarder.registerIdentityAndAddClaim(
      registryAddress,
      toAddress,
      newIdentityAddress,
      countryCode,
      kycTopic,
      1,
      CLAIM_ISSUER,
      claimSignature,
      claimData,
      '',
    );
    await registerTx.wait();
    console.log(
      '[identity] step 6b — identity registered + claim added ✓ tx:',
      registerTx.hash,
    );

    // Step 7: if still not verified, check TrustedIssuersRegistry
    console.log('[identity] step 7 — verifying recipient post-registration…');
    const nowVerified = await (registry.isVerified(
      toAddress,
    ) as Promise<boolean>);
    console.log('[identity] step 7 — isVerified:', nowVerified);
    if (!nowVerified) {
      const issuersRegistryAddress =
        await (registry.issuersRegistry() as Promise<string>);
      const issuersRegistry = new ethers.Contract(
        issuersRegistryAddress,
        TRUSTED_ISSUERS_REGISTRY_ABI,
        relayerSigner,
      ) as any;
      const isTrusted = await (issuersRegistry.isTrustedIssuer(
        CLAIM_ISSUER,
      ) as Promise<boolean>);
      console.log('[identity] step 7 — claim issuer isTrusted:', isTrusted);
      if (!isTrusted) {
        console.log('[identity] step 7 — adding trusted issuer…');
        const tx = await issuersRegistry.addTrustedIssuer(CLAIM_ISSUER, [
          kycTopic,
        ]);
        await tx.wait();
        console.log('[identity] step 7 — trusted issuer added ✓');
      }
    }

    identityRegistered = true;
    console.log('[identity] setup complete ✓');
  }

  // ── Verify sender ───────────────────────────────────────────────────────────

  const fromAddress = await treasurySigner.getAddress();
  console.log('[transfer] verifying treasury sender:', fromAddress);
  const senderVerified = await (registry.isVerified(
    fromAddress,
  ) as Promise<boolean>);
  console.log('[transfer] treasury isVerified:', senderVerified);
  if (!senderVerified)
    throw new Error(
      `Treasury wallet ${fromAddress} is not verified in the identity registry. Register it before issuing tokens.`,
    );

  // ── transferWithAuthorization ───────────────────────────────────────────────

  const amount = ethers.parseUnits(tokensHuman, decimals);
  const nonce = ethers.hexlify(ethers.randomBytes(32));

  const nonceUsed = await (token.authorizationState(
    fromAddress,
    nonce,
  ) as Promise<boolean>);
  if (nonceUsed) throw new Error('Nonce collision — please retry.');

  const { chainId } = await provider.getNetwork();
  console.log(
    '[transfer] chainId:',
    chainId.toString(),
    '| amount:',
    amount.toString(),
    '| nonce:',
    nonce,
  );

  const domain = {
    name: tokenName,
    version: tokenVersion,
    chainId,
    verifyingContract: tokenAddress,
  };
  const types = {
    TransferWithAuthorization: [
      { name: 'from', type: 'address' },
      { name: 'to', type: 'address' },
      { name: 'value', type: 'uint256' },
      { name: 'nonce', type: 'bytes32' },
    ],
  };
  const message = { from: fromAddress, to: toAddress, value: amount, nonce };

  console.log('[transfer] signing EIP-712 transferWithAuthorization…');
  const rawSig = await (treasurySigner as any).signTypedData(
    domain,
    types,
    message,
  );
  const sig = ethers.Signature.from(rawSig);
  console.log('[transfer] signed ✓ v:', sig.v);

  console.log('[transfer] submitting transferWithAuthorization…');
  const connectedToken = token.connect(relayerSigner) as any;
  const tx = await connectedToken.transferWithAuthorization(
    fromAddress,
    toAddress,
    amount,
    nonce,
    sig.v,
    sig.r,
    sig.s,
  );
  const receipt = await tx.wait();
  if (!receipt) throw new Error('Transaction receipt is null.');
  console.log(
    '[transfer] transfer confirmed ✓ block:',
    receipt.blockNumber,
    'tx:',
    receipt.hash,
  );

  // ── CRE sync ────────────────────────────────────────────────────────────────

  let creTriggered = false;
  if (creds.creWebhookUrl.trim()) {
    console.log('[transfer] triggering CRE sync…');
    try {
      await triggerCreSync(creds.creWebhookUrl, {
        token_address: tokenAddress,
        recipient_address: toAddress,
        amount: tokensHuman,
        investment_id: `transfer-${assetId}-${Date.now()}`,
        from: fromAddress,
        nonce,
        v: String(sig.v),
        r: sig.r,
        s: sig.s,
      });
      creTriggered = true;
      console.log('[transfer] CRE sync triggered ✓');
    } catch (e) {
      console.warn('[transfer] CRE sync failed (non-fatal):', e);
    }
  }

  return {
    txHash: receipt.hash as string,
    from: fromAddress,
    to: toAddress,
    tokens: tokensHuman,
    nonce,
    blockNumber: receipt.blockNumber as number,
    creTriggered,
    identityRegistered,
  };
}

// ─── CRE sync ─────────────────────────────────────────────────────────────────

async function triggerCreSync(
  webhookUrl: string,
  payload: Record<string, string>,
): Promise<void> {
  const res = await fetch(webhookUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`CRE sync returned ${res.status}`);
}
